import { Navigate, RouteObject, Outlet } from "react-router";
import { UserState } from "adminPanel/user/userSlice";
import AppContextProvider from "context/AppContextProvider";
import HttpInterceptor from "components/general/HttpInterceptor";
import { useAppSelector } from "app/hooks";
import { ReactNode, Suspense } from "react";
import React from "react";
import isNonEmptyGuid from "helpers/isNonEmptyGuid";

import UserLoginPage from "adminPanel/user/UserLoginPage";
import ErrorPage from "components/pages/ErrorPage";
import CenteredCircularProgress from "components/general/CenteredCircularProgress";
import { EmployeeSettingSlice } from "amnPos/slices/EmployeeSettingSlice";
import { IClientPagePermissions } from "models/IAmnPageControllersPermissionsBase";
import { POSType } from "amnPos/pages/UserHomePage";
import { RouterNavigateType } from "models/RouterNavigateType";

import adminRoutes from "./routes/adminRoutes";
import posRoutes from "./routes/posRoutes";
import restaurantRoutes from "./routes/restaurantRoutes";
import { PermissionType } from "amnShared/Permissions/IPermissions";
import ApprovalGuard from "./ApprovalGuard";
import ForgetPasswordPage from "components/pages/ForgetPasswordPage";
import ResetPasswordPage from "components/pages/ResetPasswordPage";
import getOrganization from "helpers/getOrganization";
const OrganizationSignUpPage = React.lazy(
  () => import("adminPanel/organization/OrganizationSignUpPage"),
);
const EmailValidationPage = React.lazy(
  () => import("adminPanel/user/EmailValidationPage"),
);
const VerifyYourEmailPage = React.lazy(
  () => import("adminPanel/organization/VerifyYourEmailPage"),
);
const PosLoginPage = React.lazy(() => import("amnPos/pages/PosLoginPage"));
const UserHomePage = React.lazy(() => import("amnPos/pages/UserHomePage"));
const NoPermissionsPage = React.lazy(
  () => import("components/pages/NoPermissionsPage"),
);
const SectionTablePage = React.lazy(
  () => import("adminPanel/stores/sectionSettings/SectionTablePage"),
);
const PublicMenuPage = React.lazy(
  () => import("components/pages/PublicMenuPage"),
);
const PublicStoreMenusPage = React.lazy(
  () => import("components/pages/PublicStoreMenusPage"),
);
const InternalLoginPage = React.lazy(
  () => import("adminPanel/internalConsole/InternalLoginPage"),
);
const InternalConsoleLayout = React.lazy(
  () => import("adminPanel/internalConsole/InternalConsoleLayout"),
);
const InternalOverviewPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/OverviewPage"),
);
const InternalTenantsPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/TenantsPage"),
);
const InternalTenantDetailPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/TenantDetailPage"),
);
const InternalSystemHealthPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/SystemHealthPage"),
);
const InternalBusinessPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/BusinessPage"),
);
const InternalAdoptionPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/AdoptionPage"),
);
const InternalSubscriptionsPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/SubscriptionsPage"),
);
const InternalUsersPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/UsersPage"),
);
const InternalRolesPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/RolesPage"),
);
const InternalProfilePage = React.lazy(
  () => import("adminPanel/internalConsole/pages/ProfilePage"),
);
const InternalNotificationsPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/NotificationsPage"),
);
const InternalNotificationsSettingsPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/NotificationsSettingsPage"),
);
const InternalAuditLogPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/AuditLogPage"),
);
const InternalAuditSettingsPage = React.lazy(
  () => import("adminPanel/internalConsole/pages/AuditSettingsPage"),
);
export type RouteConfig = RouteObject;

const isInternalConsoleEnabled =
  import.meta.env.VITE_INTERNAL_CONSOLE_ENABLED === "true";

const WrapSuspense = (component: ReactNode) => (
  <Suspense fallback={<CenteredCircularProgress />}>{component}</Suspense>
);

/**
 * AppLayout - Wraps all routes with AppContextProvider INSIDE the router.
 * This ensures dialogs can access router hooks (useNavigate, useParams, etc.)
 */
function AppLayout() {
  return (
    <AppContextProvider>
      <HttpInterceptor>
        <Outlet />
      </HttpInterceptor>
    </AppContextProvider>
  );
}

export default function useRoutes() {
  const user = useAppSelector((state) => state.user as UserState);
  const posUser = useAppSelector(
    (state) => state.employeeSettings as EmployeeSettingSlice,
  );
  const isLoggedIn = isNonEmptyGuid(user.id) && !user.isCasher;
  const isPosLoggedIn = isNonEmptyGuid(user.id) && user.isCasher;
  const posId = localStorage.getItem("posId");
  const posType = localStorage.getItem("posType");

  const getClientPagePermissions = (): IClientPagePermissions | undefined => {
    if (isLoggedIn) return undefined;
    return {
      canAdd: PermissionType.NONE,
      canResetAndClear: PermissionType.NONE,
      canModify: PermissionType.CanModifyExistingClient,
      addressPageControllersPermissions: {
        canResetAndClear: PermissionType.CanAddNewAddress,
        canAdd: PermissionType.NONE,
        canModify: PermissionType.CanModifyAddress,
        canChangeDefaultCountryAndProvince:
          PermissionType.CanChangeDefaultCountryAndProvince,
      },
    };
  };

  const getPath = (pathName: string) =>
    `/${
      user.organizationId || getOrganization() || ":organizationId"
    }${pathName}`;

  const LoggedInPosHome = (component: ReactNode) => {
    let target = component;
    if (!isPosLoggedIn) target = <Navigate to={getPath("/login")} />;
    if (isNonEmptyGuid(user.id) && !user.isCasher)
      target = <Navigate to={getPath("/permissions")} />;
    return WrapSuspense(target);
  };

  const LoggedInPos = (component: ReactNode, permission?: PermissionType) => {
    let target = component;
    if (isNonEmptyGuid(user.id) || isNonEmptyGuid(user.posUserId)) {
      if (!user.isCasher) target = <Navigate to={getPath("/permissions")} />;
      if (posType && posType !== POSType.POS.toString())
        target = <Navigate to={getPath("/restaurants")} />;
    } else {
      target = <Navigate to={getPath("/login")} />;
    }

    if (permission) {
      target = (
        <ApprovalGuard
          key={permission}
          permissionType={permission}
          redirectPath={getPath("/permissions")}
        >
          {target}
        </ApprovalGuard>
      );
    }
    return WrapSuspense(target);
  };

  const LoggedInRest = (component: ReactNode, permission?: PermissionType) => {
    let target = component;
    if (isNonEmptyGuid(user.id) || isNonEmptyGuid(user.posUserId)) {
      if (!user.isCasher) target = <Navigate to={getPath("/permissions")} />;
      if (posType && posType !== POSType.Restaurants.toString())
        target = <Navigate to={getPath("/pos")} />;
    } else {
      target = <Navigate to={getPath("/login")} />;
    }

    if (permission) {
      target = (
        <ApprovalGuard
          key={permission}
          permissionType={permission}
          redirectPath={getPath("/restaurants/materialsTransactions")}
        >
          {target}
        </ApprovalGuard>
      );
    }

    return WrapSuspense(target);
  };

  const LoggedIn = (component: ReactNode) => {
    let target = component;
    if (!isLoggedIn) target = <Navigate to={getPath("/login")} />;
    if (isNonEmptyGuid(user.id) && user.isCasher)
      target = <Navigate to={getPath("/permissions")} />;
    return WrapSuspense(target);
  };

  const AllowGetPage = (component: ReactNode) => {
    let target = component;
    if (isLoggedIn) target = <Navigate to={getPath("/")} />;
    else if (isPosLoggedIn && posId) {
      target =
        posType === POSType.Restaurants.toString() ? (
          <Navigate to={getPath("/restaurants")} />
        ) : (
          <Navigate to={getPath("/pos")} />
        );
    } else if (isPosLoggedIn) target = <Navigate to={getPath("/home")} />;
    return WrapSuspense(target);
  };

  // All routes are wrapped in AppLayout which provides context INSIDE the router
  const routes: RouteConfig[] = [
    {
      element: <AppLayout />,
      errorElement: <ErrorPage />,
      children: [
        { path: "/", element: <UserLoginPage /> },
        { path: getPath("/error"), element: <ErrorPage /> },
        { path: "/verify/:id", element: AllowGetPage(<EmailValidationPage />) },
        { path: "/login", element: AllowGetPage(<UserLoginPage />) },
        { path: "/signup", element: AllowGetPage(<OrganizationSignUpPage />) },
        {
          path: "/forget-password",
          element: AllowGetPage(<ForgetPasswordPage />),
        },
        {
          path: "/reset-password",
          element: AllowGetPage(<ResetPasswordPage />),
        },
        {
          path: "/verify-your-email",
          element: AllowGetPage(<VerifyYourEmailPage />),
        },
        {
          path: "/public-menu",
          element: WrapSuspense(<PublicMenuPage />),
        },
        {
          path: "/public-menus",
          element: WrapSuspense(<PublicStoreMenusPage />),
        },
        ...(isInternalConsoleEnabled
          ? [
              {
                path: "/internal/login",
                element: WrapSuspense(<InternalLoginPage />),
              },
              {
                path: "/internal",
                element: WrapSuspense(<InternalConsoleLayout />),
                children: [
                  {
                    index: true,
                    element: WrapSuspense(<InternalOverviewPage />),
                  },
                  {
                    path: "tenants",
                    element: WrapSuspense(<InternalTenantsPage />),
                  },
                  {
                    path: "tenants/:id",
                    element: WrapSuspense(<InternalTenantDetailPage />),
                  },
                  {
                    path: "system-health",
                    element: WrapSuspense(<InternalSystemHealthPage />),
                  },
                  {
                    path: "business",
                    element: WrapSuspense(<InternalBusinessPage />),
                  },
                  {
                    path: "adoption",
                    element: WrapSuspense(<InternalAdoptionPage />),
                  },
                  {
                    path: "subscriptions",
                    element: WrapSuspense(<InternalSubscriptionsPage />),
                  },
                  {
                    path: "users",
                    element: WrapSuspense(<InternalUsersPage />),
                  },
                  {
                    path: "roles",
                    element: WrapSuspense(<InternalRolesPage />),
                  },
                  {
                    path: "profile",
                    element: WrapSuspense(<InternalProfilePage />),
                  },
                  {
                    path: "notifications",
                    element: WrapSuspense(<InternalNotificationsPage />),
                  },
                  {
                    path: "notification-settings",
                    element: WrapSuspense(
                      <InternalNotificationsSettingsPage />,
                    ),
                  },
                  {
                    path: "audit-logs",
                    element: WrapSuspense(<InternalAuditLogPage />),
                  },
                  {
                    path: "audit-settings",
                    element: WrapSuspense(<InternalAuditSettingsPage />),
                  },
                ],
              },
            ]
          : []),
        { path: getPath("/login"), element: AllowGetPage(<UserLoginPage />) },
        {
          path: getPath("/forget-password"),
          element: AllowGetPage(<ForgetPasswordPage />),
        },
        {
          path: getPath("/reset-password"),
          element: AllowGetPage(<ResetPasswordPage />),
        },
        {
          path: getPath("/login-pos"),
          element: AllowGetPage(<PosLoginPage />),
        },
        {
          path: getPath("/permissions"),
          element: AllowGetPage(<NoPermissionsPage />),
        },
        { path: getPath("/home"), element: LoggedInPosHome(<UserHomePage />) },
        {
          path: getPath("/section-table/:storeId"),
          element: LoggedIn(<SectionTablePage />),
        },
        adminRoutes({
          getPath,
          LoggedIn,
          AllowGetPage,
          RouterNavigateType,
          getClientPagePermissions,
        }),
        posRoutes({
          getPath,
          LoggedInPos,
          getClientPagePermissions,
          RouterNavigateType,
        }),
        restaurantRoutes({
          getPath,
          LoggedInRest,
          getClientPagePermissions,
          RouterNavigateType,
        }),
        { path: "*", element: <div>Invalid route</div> },
      ],
    },
  ];

  return routes;
}
